import { useCallback, useContext, useState } from 'react'
import { AuthContext } from '../../../context'
import { useNavigate } from 'react-router-dom'
import AuthService from '../../../services/auth-service'
import useMounted from '../../../hooks/useMounted'
import { generatePassword } from '../../../utils'

const useRegister = () => {
  const authContext = useContext(AuthContext)
  const [success, setSuccess] = useState(false)
  const isMounted = useMounted()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [inputs, setInputs] = useState({
    name: '',
    email: '',
    password: '',
    confirm: '',
    agree: false,
  })

  const [errors, setErrors] = useState({
    nameError: false,
    emailError: false,
    existError: false,
    passwordError: false,
    confirmError: false,
    agreeError: false,
    error: false,
    errorText: '',
  })

  const handleChange = useCallback(
    e => {
      setInputs({
        ...inputs,
        [e.target.name]: e.target.value,
      })
    },
    [inputs],
  )

  const handleCheckbox = useCallback(
    e => {
      setInputs({
        ...inputs,
        [e.target.name]: e.target.checked,
      })
    },
    [inputs],
  )
  const handleGeneratePassword = () => {
    const newPassword = generatePassword()
    setInputs(prev => ({
      ...prev,
      password: newPassword,
      confirm: newPassword,
    }))
    try {
      navigator.clipboard.writeText(newPassword)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()

      const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
      let errors = {}

      if (inputs?.name?.length < 3) {
        errors = { ...errors, nameError: true }
      }

      if (
        inputs?.email?.trim().length === 0 ||
        !inputs?.email?.trim().match(mailFormat)
      ) {
        errors = { ...errors, emailError: true }
      }

      if (inputs?.password?.trim().length < 8) {
        errors = { ...errors, passwordError: true }
      }

      if (inputs?.confirm !== inputs?.password) {
        errors = { ...errors, confirmError: true }
      }

      if (inputs?.agree !== true) {
        errors = { ...errors, agreeError: true }
      }

      setErrors(errors)
      if (Object.keys(errors).length > 0) {
        return
      }

      const newUser = {
        name: inputs?.name,
        email: inputs?.email,
        password: inputs?.password,
      }

      setSuccess(null)
      try {
        setIsLoading(true)
        setSuccess(true)
        const response = await AuthService.register(newUser)
        authContext.login(response.access_token, true)
        setErrors({
          emailError: false,
          existError: false,
          passwordError: false,
          confirmError: false,
          agreeError: false,
          error: false,
          errorText: '',
        })
        setInputs({
          email: '',
          password: '',
          confirm: '',
          agree: false,
          name: '',
        })
      } catch (err) {
        if (err.response.status === 400) {
          setErrors(current => ({
            ...current,
            existError: true,
          }))
        } else {
          setErrors({
            ...errors,
            error: true,
            errorText:
              'A apărut o eroare neprevazută. Dacă persistă, te rugăm să ne contactezi.',
          })
          console.error(err)
        }
        setSuccess(false)
      } finally {
        setIsLoading(false)
      }
    },
    [inputs, authContext],
  )

  const handleGoogleLoginSuccess = useCallback(
    async res => {
      const myData = {
        data: {
          type: 'google_token',
          token: res.credential,
        },
      }

      setSuccess(null)
      try {
        setIsLoading(true)
        const response = await AuthService.login(myData)
        setSuccess(true)
        if (response.hasOwnProperty('access_token')) {
          authContext.login(response.access_token, true)
        } else {
          navigate(
            `/resetare-parola?token=${response.register_token}&registering=1`,
          )
        }
      } catch (err) {
        console.log(err)
        // Should not get here
        setSuccess(false)
      } finally {
        setIsLoading(false)
      }
    },
    [authContext, navigate],
  )

  const handleGoogleLoginError = useCallback(error => {
    console.log(error)
  }, [])

  return {
    handleGoogleLoginError,
    handleSubmit,
    errors,
    inputs,
    handleChange,
    handleGeneratePassword,
    success,
    handleCheckbox,
    handleGoogleLoginSuccess,
    isLoading,
  }
}

export default useRegister
